import ApiService from '@services/api.service'
import { iri, unwrap } from 'src/utils/apiPlatform'
import { normalizeProspect, normalizeRelance, normalizeType } from 'src/utils/prospection'

// ════════════════════════════════════════════════════════════════
// Store Prospection
// ════════════════════════════════════════════════════════════════
// Même structure que les autres modules (state / mutations / actions /
// getters + ApiService), avec trois différences volontaires :
//
// 1. namespaced: true → dispatch('prospection/fetchProspectsEntreprise').
//    Les mutations et actions ne peuvent pas entrer en collision avec
//    celles d'un autre module (ex. un autre `setActions`).
//
// 2. Un emplacement d'état par usage : la liste de la fiche entreprise
//    (`prospects`) et la liste de l'accueil (`mesProspects`) ne
//    s'écrasent pas l'une l'autre.
//
// 3. Les actions RENVOIENT le résultat et RELANCENT l'erreur, sans
//    Notify : le formulaire a besoin de l'erreur 422 pour afficher le
//    message sous le bon champ ("Cet intitulé existe déjà"), et les
//    composants choisissent eux-mêmes leur notification.

const RESOURCE_PROSPECT = 'entreprise_prospects'
const RESOURCE_TYPE = 'type_prospections'
const RESOURCE_RELANCE = 'entreprise_prospect_relances'

// Requêtes d'intitulés en vol, par antenne : deux sélecteurs montés en
// même temps ne déclenchent qu'un seul appel. Hors du state car une
// promesse n'est pas une donnée d'état.
const typesEnVol = {}

const trierTypes = (a, b) =>
  (a.ordre - b.ordre) || a.libelle.localeCompare(b.libelle, 'fr', { sensitivity: 'base' })

export const prospection = {
  namespaced: true,

  state: () => ({
    // Fiche entreprise
    entrepriseId: null,
    prospects: [],
    // Page d'accueil / tableau de bord
    mesProspects: [],
    // Intitulés, par antenne : { [antenneId]: TypeProspection[] }
    typesParAntenne: {},
    // Relances de l'entreprise affichée (timeline)
    relancesEntrepriseId: null,
    relances: []
  }),

  mutations: {
    resetProspects (state, entrepriseId) {
      state.entrepriseId = entrepriseId
      state.prospects = []
    },
    setProspects (state, { entrepriseId, prospects }) {
      // Si l'utilisateur a changé de fiche entre-temps, on ignore la
      // réponse tardive de l'entreprise précédente.
      if (state.entrepriseId !== entrepriseId) return
      state.prospects = prospects
    },
    setMesProspects (state, prospects) {
      state.mesProspects = prospects
    },
    upsertProspect (state, prospect) {
      const remplacer = (liste) => {
        const index = liste.findIndex(p => p.id === prospect.id)
        if (index !== -1) liste.splice(index, 1, prospect)
        return index !== -1
      }
      if (!remplacer(state.prospects) && prospect.entreprise?.id === state.entrepriseId) {
        state.prospects.unshift(prospect)
      }
      remplacer(state.mesProspects)
    },
    removeProspect (state, id) {
      state.prospects = state.prospects.filter(p => p.id !== id)
      state.mesProspects = state.mesProspects.filter(p => p.id !== id)
    },
    resetRelances (state, entrepriseId) {
      state.relancesEntrepriseId = entrepriseId
      state.relances = []
    },
    setRelances (state, { entrepriseId, relances }) {
      if (state.relancesEntrepriseId !== entrepriseId) return
      state.relances = relances
    },
    upsertRelance (state, relance) {
      const index = state.relances.findIndex(r => r.id === relance.id)
      if (index === -1) state.relances.push(relance)
      else state.relances.splice(index, 1, relance)
    },
    setTypes (state, { antenneId, types }) {
      state.typesParAntenne = { ...state.typesParAntenne, [antenneId]: types }
    },
    addType (state, { antenneId, type }) {
      const types = [...(state.typesParAntenne[antenneId] ?? []), type].sort(trierTypes)
      state.typesParAntenne = { ...state.typesParAntenne, [antenneId]: types }
    }
  },

  actions: {
    // ── Opportunités ──────────────────────────────────────────────

    // Opportunités d'une entreprise (onglet Prospection).
    // pagination=false nécessite `paginationClientEnabled: true` côté API.
    async fetchProspectsEntreprise ({ commit }, entrepriseId) {
      commit('resetProspects', entrepriseId)
      const response = await ApiService.getRequest(RESOURCE_PROSPECT, {
        entreprise: entrepriseId,
        pagination: false
      })
      const prospects = unwrap(response.data).map(normalizeProspect)
      commit('setProspects', { entrepriseId, prospects })
      return prospects
    },

    // Opportunités filtrées (accueil, tableau de bord), ex. :
    // { responsables: 12, 'statut[]': STATUTS_EN_COURS,
    //   'order[date_echeance]': 'asc', itemsPerPage: 5 }
    // (noms de propriétés en snake_case, comme le reste de l'API)
    async fetchMesProspects ({ commit }, filters = {}) {
      const response = await ApiService.getRequest(RESOURCE_PROSPECT, filters)
      const prospects = unwrap(response.data).map(normalizeProspect)
      commit('setMesProspects', prospects)
      return prospects
    },

    async createProspect ({ commit }, payload) {
      const response = await ApiService.postRequest(RESOURCE_PROSPECT, payload)
      const prospect = normalizeProspect(response.data)
      commit('upsertProspect', prospect)
      return prospect
    },

    async updateProspect ({ commit }, { id, payload }) {
      const response = await ApiService.patchRequest(`${RESOURCE_PROSPECT}/${id}`, payload)
      const prospect = normalizeProspect(response.data)
      commit('upsertProspect', prospect)
      return prospect
    },

    // ⚠ Méthode supposée : adapter si ApiService la nomme autrement
    async deleteProspect ({ commit }, id) {
      await ApiService.deleteRequest(`${RESOURCE_PROSPECT}/${id}`)
      commit('removeProspect', id)
    },

    // Recharge UNE opportunité : après un changement de relance, le
    // serveur a recalculé sa prochaine relance et sa date_relance.
    async refreshProspect ({ commit }, id) {
      const response = await ApiService.getRequest(`${RESOURCE_PROSPECT}/${id}`)
      const prospect = normalizeProspect(response.data)
      commit('upsertProspect', prospect)
      return prospect
    },

    // ── Relances ──────────────────────────────────────────────────

    // Toutes les relances des opportunités d'une entreprise (timeline)
    async fetchRelancesEntreprise ({ commit }, entrepriseId) {
      commit('resetRelances', entrepriseId)
      const response = await ApiService.getRequest(RESOURCE_RELANCE, {
        'prospect.entreprise': entrepriseId,
        pagination: false
      })
      const relances = unwrap(response.data).map(normalizeRelance)
      commit('setRelances', { entrepriseId, relances })
      return relances
    },

    async createRelance ({ commit, dispatch }, payload) {
      const response = await ApiService.postRequest(RESOURCE_RELANCE, payload)
      const relance = normalizeRelance(response.data)
      commit('upsertRelance', relance)
      await dispatch('refreshProspectDeRelance', relance)
      return relance
    },

    async updateRelance ({ commit, dispatch }, { id, payload }) {
      const response = await ApiService.patchRequest(`${RESOURCE_RELANCE}/${id}`, payload)
      const relance = normalizeRelance(response.data)
      commit('upsertRelance', relance)
      await dispatch('refreshProspectDeRelance', relance)
      return relance
    },

    // Le rafraîchissement de la carte est secondaire : s'il échoue, la
    // relance est quand même enregistrée, on ne fait pas échouer l'action.
    async refreshProspectDeRelance ({ dispatch }, relance) {
      const id = relance?.prospectIri ? relance.prospectIri.split('/').pop() : null
      if (!id) return
      try {
        await dispatch('refreshProspect', id)
      } catch (e) {
        console.error('Rafraîchissement de l\'opportunité impossible :', e)
      }
    },

    // ── Intitulés ─────────────────────────────────────────────────

    // Chargés une seule fois par antenne ; force: true pour rafraîchir.
    fetchTypes ({ commit, state }, { antenneId, force = false }) {
      if (antenneId === null || antenneId === undefined) return Promise.resolve([])
      if (typesEnVol[antenneId]) return typesEnVol[antenneId]
      if (!force && state.typesParAntenne[antenneId]) {
        return Promise.resolve(state.typesParAntenne[antenneId])
      }

      typesEnVol[antenneId] = ApiService.getRequest(RESOURCE_TYPE, { antenne: antenneId })
        .then((response) => {
          const types = unwrap(response.data).map(normalizeType).sort(trierTypes)
          commit('setTypes', { antenneId, types })
          return types
        })
        .finally(() => { delete typesEnVol[antenneId] })

      return typesEnVol[antenneId]
    },

    async createType ({ commit }, { antenneId, libelle }) {
      const response = await ApiService.postRequest(RESOURCE_TYPE, {
        libelle,
        antenne: iri('antennes', antenneId)
      })
      const type = normalizeType(response.data)
      commit('addType', { antenneId, type })
      return type
    }
  },

  getters: {
    getProspects: state => state.prospects,
    getProspectsEntrepriseId: state => state.entrepriseId,
    getMesProspects: state => state.mesProspects,
    getTypes: state => antenneId => state.typesParAntenne[antenneId] ?? [],
    getRelances: state => state.relances,
    getRelancesEntrepriseId: state => state.relancesEntrepriseId
  }
}
